import React, { useState, useEffect, useRef, useCallback } from 'react';
import './MandalaTracker.css';

const CENTER_X = 350;
const CENTER_Y = 350;
const CENTER_RADIUS = 50;
const PETAL_COUNT = 30;
const PETAL_LENGTH = 120;
const PETAL_WIDTH = 58;

const STORAGE_KEYS = {
  DAYS: 'mandalaDays'
};

export const MandalaTracker = () => {
  const [filledDays, setFilledDays] = useState(() => {
    // Initialize from localStorage on first render
    const savedDays = localStorage.getItem(STORAGE_KEYS.DAYS);
    if (savedDays) {
      try {
        return new Set(JSON.parse(savedDays));
      } catch (e) {
        console.error('Failed to load mandala days:', e);
      }
    }
    return new Set();
  });
  const svgRef = useRef(null);

  // Save to localStorage when data changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.DAYS, JSON.stringify([...filledDays]));
  }, [filledDays]);

  // Create petal path data
  const createPetalPath = useCallback((angle) => {
    const angleRad = (angle - 90) * Math.PI / 180;
    const startX = CENTER_X + Math.cos(angleRad) * (CENTER_RADIUS + 5);
    const startY = CENTER_Y + Math.sin(angleRad) * (CENTER_RADIUS + 5);
    const endX = CENTER_X + Math.cos(angleRad) * (CENTER_RADIUS + PETAL_LENGTH);
    const endY = CENTER_Y + Math.sin(angleRad) * (CENTER_RADIUS + PETAL_LENGTH);

    const path = `
      M ${startX} ${startY}
      Q ${startX + Math.cos(angleRad + 0.3) * PETAL_WIDTH} ${startY + Math.sin(angleRad + 0.3) * PETAL_WIDTH}
        ${endX} ${endY}
      Q ${startX + Math.cos(angleRad - 0.3) * PETAL_WIDTH} ${startY + Math.sin(angleRad - 0.3) * PETAL_WIDTH}
        ${startX} ${startY}
    `;

    return { path, angleRad };
  }, []);

  // Toggle day completion - supports both click and touch
  const toggleDay = useCallback((day) => {
    setFilledDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(day)) {
        newSet.delete(day);
      } else {
        newSet.add(day);
      }
      return newSet;
    });
  }, []);

  // Handle touch events to prevent double-firing
  const handleInteraction = useCallback((e, day) => {
    e.preventDefault();
    toggleDay(day);
  }, [toggleDay]);

  // Reset tracker
  const handleReset = useCallback(() => {
    setFilledDays(new Set());
  }, []);

  // Generate petals (Days 2-31)
  const petals = [];
  for (let i = 0; i < PETAL_COUNT; i++) {
    const angle = (360 / PETAL_COUNT) * i;
    const dayNum = i + 2;
    const { path, angleRad } = createPetalPath(angle);

    // Calculate text position
    const textX = CENTER_X + Math.cos(angleRad) * (CENTER_RADIUS + PETAL_LENGTH + 20);
    const textY = CENTER_Y + Math.sin(angleRad) * (CENTER_RADIUS + PETAL_LENGTH + 20);

    petals.push(
      <g key={`petal-${dayNum}`}>
        <path
          d={path}
          className={`mandala-petal ${filledDays.has(dayNum) ? 'filled' : ''}`}
          data-day={dayNum}
          data-testid={`mandala-petal-${dayNum}`}
          onClick={(e) => handleInteraction(e, dayNum)}
          onTouchEnd={(e) => handleInteraction(e, dayNum)}
        />
        <text
          x={textX}
          y={textY}
          className="mandala-day-number"
        >
          {dayNum}
        </text>
      </g>
    );
  }

  return (
    <div className="mandala-container" data-testid="mandala-tracker">
      <div className="mandala-tracker-wrapper">
        <h2 className="mandala-title">Your Mandala Tracker</h2>
        <div className="mandala-progress" data-testid="mandala-progress">
          Days completed: <span className="mandala-progress-number">{filledDays.size}</span> / 30
        </div>

        <svg
          ref={svgRef}
          className="mandala-svg"
          viewBox="140 140 420 420"
          data-testid="mandala-svg"
        >
          {/* Petals (Days 2-31) */}
          {petals}

          {/* Center Circle (Day 1) */}
          <circle
            cx={CENTER_X}
            cy={CENTER_Y}
            r={CENTER_RADIUS}
            className={`mandala-center-circle ${filledDays.has(1) ? 'filled' : ''}`}
            data-testid="mandala-center-day1"
            onClick={(e) => handleInteraction(e, 1)}
            onTouchEnd={(e) => handleInteraction(e, 1)}
          />

          {/* Center text */}
          <text
            x={CENTER_X}
            y={CENTER_Y}
            className="mandala-center-text"
          >
            Day 1
          </text>
        </svg>
      </div>

      <div className="mandala-controls">
        <button
          className="mandala-btn mandala-reset-btn"
          onClick={handleReset}
          data-testid="mandala-reset-btn"
        >
          Reset Mandala
        </button>
      </div>
    </div>
  );
};

export default MandalaTracker;
